import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import './AlarmAnalysis.css';

function AlarmAnalysis() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // URL 파라미터에서 정보 가져오기
  const machine = searchParams.get('machine');
  const alarmId = searchParams.get('alarmId');

  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 향후 API 연동을 위한 준비
    const loadAlarmData = async () => {
      try {
        setLoading(true);
        // TODO: API 호출 로직 추가
        // const data = await fetchAlarmDetail(alarmId);
        setError(null);
      } catch (err) {
        console.error('알람 데이터 조회 실패:', err);
        setError(t('pages.monitoring.alarmAnalysis.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    if (alarmId) {
      loadAlarmData();
    } else {
      setLoading(false);
    }
  }, [alarmId, t]);

  return (
    <MainLayout>
      <div className="alarm-analysis-container">
        {/* 헤더 */}
        <div className="alarm-analysis-header">
          <div className="alarm-analysis-header-left">
            <button className="back-button" onClick={() => navigate(-1)}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <div>
              <h1 className="alarm-analysis-title">{t('pages.monitoring.alarmAnalysis.title')}</h1>
              <p className="alarm-analysis-subtitle">
                {machine && t('pages.monitoring.alarmAnalysis.machinePrefix', { name: machine })}
                {machine && alarmId && ' | '}
                {alarmId && t('pages.monitoring.alarmAnalysis.alarmIdPrefix', { id: alarmId })}
              </p>
            </div>
          </div>
        </div>

        {/* 로딩 상태 */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner">
              <span className="material-symbols-outlined rotating">progress_activity</span>
              <div>{t('pages.monitoring.alarmAnalysis.loading')}</div>
            </div>
          </div>
        )}

        {/* 에러 상태 */}
        {error && !loading && (
          <div className="error-container">
            <div className="error-content">
              <span className="material-symbols-outlined">error</span>
              <div>
                <div className="error-title">{t('pages.monitoring.alarmAnalysis.loadFailedTitle')}</div>
                <div className="error-message">{error}</div>
              </div>
            </div>
            <button className="retry-button" onClick={() => window.location.reload()}>
              {t('pages.monitoring.alarmAnalysis.retry')}
            </button>
          </div>
        )}

        {/* 메인 콘텐츠 */}
        {!loading && !error && (
          <div className="alarm-analysis-content">
            {/* 알람 정보 카드 */}
            <div className="alarm-info-card">
              <h2 className="section-title">{t('pages.monitoring.alarmAnalysis.info.title')}</h2>
              <div className="info-grid">
                <div className="info-item">
                  <div className="info-label">{t('pages.monitoring.alarmAnalysis.info.alarmId')}</div>
                  <div className="info-value">{alarmId || '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('pages.monitoring.alarmAnalysis.info.machine')}</div>
                  <div className="info-value">{machine || '-'}</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('pages.monitoring.alarmAnalysis.info.occurredAt')}</div>
                  <div className="info-value">-</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('pages.monitoring.alarmAnalysis.info.duration')}</div>
                  <div className="info-value">-</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('pages.monitoring.alarmAnalysis.info.severity')}</div>
                  <div className="info-value severity-error">-</div>
                </div>
                <div className="info-item">
                  <div className="info-label">{t('pages.monitoring.alarmAnalysis.info.status')}</div>
                  <div className="info-value">-</div>
                </div>
              </div>
            </div>

            {/* 알람 메시지 */}
            <div className="alarm-message-card">
              <h2 className="section-title">{t('pages.monitoring.alarmAnalysis.message.title')}</h2>
              <div className="message-content">
                <p>{t('pages.monitoring.alarmAnalysis.message.description')}</p>
                <p className="message-note">
                  {t('pages.monitoring.alarmAnalysis.message.note')}
                </p>
              </div>
            </div>

            {/* 추가 분석 정보 영역 */}
            <div className="alarm-analysis-grid">
              <div className="analysis-card">
                <h2 className="section-title">{t('pages.monitoring.alarmAnalysis.cause.title')}</h2>
                <div className="analysis-content">
                  <p>{t('pages.monitoring.alarmAnalysis.cause.placeholder')}</p>
                </div>
              </div>

              <div className="analysis-card">
                <h2 className="section-title">{t('pages.monitoring.alarmAnalysis.action.title')}</h2>
                <div className="analysis-content">
                  <p>{t('pages.monitoring.alarmAnalysis.action.placeholder')}</p>
                </div>
              </div>
            </div>

            {/* API 연동 안내 */}
            <div className="api-notice">
              <span className="material-symbols-outlined">info</span>
              <div>
                <strong>{t('pages.monitoring.alarmAnalysis.apiNotice.label')}</strong> {t('pages.monitoring.alarmAnalysis.apiNotice.description')}
                <br />
                {t('pages.monitoring.alarmAnalysis.apiNotice.endpoint')} <code>GET /api/dashboard/alarm/{'{alarmId}'}</code>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

export default AlarmAnalysis;
